import { useEffect } from "react";
import { playElegantClick, playKeyTap, getSoundSettings } from "@/lib/sound";

/**
 * GlobalSoundFX — mounts ONCE at the app root and installs document-level
 * pointerdown + keydown listeners that play a short, classy tap.
 *
 * Design rules to keep it tasteful (NO mamón):
 *   - Click sound only on actual interactive targets: <button>, <a>,
 *     <input type=submit|button|checkbox|radio>, <summary>, role=button,
 *     elements with [data-clickable] or onclick. Plain text/divs are silent.
 *   - Key tap only on printable keys + Enter/Space/Backspace inside form
 *     fields (so typing emails feels alive, but Tab/arrows stay silent).
 *   - Repeats throttled to avoid Doppler when somebody holds a key.
 *   - Disabled when [data-no-sound] is on the element or any ancestor.
 *   - Auto-resumes the AudioContext on the first gesture (browsers block
 *     audio until then).
 */

const KEY_THROTTLE_MS = 35;
let lastKeyAt = 0;

function isInteractive(target) {
  if (!target || !target.closest) return false;
  if (target.closest("[data-no-sound]")) return false;
  return !!target.closest(
    [
      "button",
      "a[href]",
      "summary",
      "label[for]",
      "[role='button']",
      "[role='tab']",
      "[role='menuitem']",
      "[role='switch']",
      "[role='checkbox']",
      "[role='radio']",
      "input[type='button']",
      "input[type='submit']",
      "input[type='checkbox']",
      "input[type='radio']",
      "input[type='reset']",
      "input[type='file']",
      "select",
      "[data-clickable]",
    ].join(",")
  );
}

function isTypableKey(e) {
  // Ignore modifier-only presses
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  const k = e.key;
  if (!k) return false;
  // Single printable char OR specific control keys we want to chime on
  if (k.length === 1) return true;
  return ["Enter", "Backspace", "Tab", "Space", " "].includes(k);
}

function isFormField(target) {
  if (!target) return false;
  const tag = (target.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea") return true;
  if (target.isContentEditable) return true;
  return false;
}

export default function GlobalSoundFX() {
  useEffect(() => {
    const onPointerDown = (e) => {
      // primary button / touch only
      if (e.button !== undefined && e.button !== 0) return;
      if (!isInteractive(e.target)) return;
      // Distinct pitch per element so back-to-back clicks don't sound identical
      const seed = (e.clientX + e.clientY) % 100 / 100;
      try { playElegantClick(seed); } catch { /* ignore */ }
    };

    const onKeyDown = (e) => {
      if (!getSoundSettings().enabled) return;
      if (e.repeat) return; // ignore held-down keys
      if (!isFormField(e.target)) return;
      if (e.target.closest && e.target.closest("[data-no-sound]")) return;
      if (!isTypableKey(e)) return;
      const now = performance.now();
      if (now - lastKeyAt < KEY_THROTTLE_MS) return;
      lastKeyAt = now;
      const seed = ((e.key || "x").charCodeAt(0) % 17) / 17;
      try { playKeyTap(seed); } catch { /* ignore */ }
    };

    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("keydown", onKeyDown, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return null;
}
